import React, {useEffect, useState} from "react";
import {colors} from "nxtconstants";
import styled from "styled-components";
import useIsMobileView from "components/fpd/commons/useMobileView";

const TestimonialBento = ({testimonialData = {}}) => {
  const {
    headerIcon = "",
    headerText = "",
    heading = "",
    testimonials = [],
    barAvatars = [],
    barText = "",
    viewAllText = "",
    showLessText = ""
  } = testimonialData;

  const [mobileIdx, setMobileIdx] = useState(0);
  const isMobile = useIsMobileView();
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!isMobile || testimonials.length === 0) return;
    const timer = setInterval(() => {
      setMobileIdx((prev) => (prev + 1) % testimonials.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [isMobile, testimonials.length]);

  return (
    <GallerySection>
      <TopSection>
        <TopIcon
          src={headerIcon}
          alt="testimonial icon"
          width={24}
          height={24}
          decoding="async"
        />
        <TopText>{headerText}</TopText>
      </TopSection>
      <GalleryHeading>{heading}</GalleryHeading>
      <Grid>
        {isMobile ? (
          testimonials.length > 0 && (
            <Card className={`card-${mobileIdx + 1}`}>
              <CardTitle>{testimonials[mobileIdx].title}</CardTitle>
              <Stars>
                {Array.from({length: 5}).map((_, idx) => (
                  <span key={idx}>{idx < testimonials[mobileIdx].stars ? "★" : "☆"}</span>
                ))}
              </Stars>
              <CardText>{testimonials[mobileIdx].text}</CardText>
              <UserRow>
                <Avatar
                  src={testimonials[mobileIdx].avatar}
                  alt={testimonials[mobileIdx].name}
                  width={70}
                  height={70}
                  decoding="async"
                />
                <UserInfo>
                  <UserName>{testimonials[mobileIdx].name}</UserName>
                  <UserRole>{testimonials[mobileIdx].role}</UserRole>
                </UserInfo>
              </UserRow>
            </Card>
          )
        ) : (
          testimonials
            .filter((_, i) => showAll || i < 9)
            .map((t, i) => {
              return (
                <Card key={i} className={`card-${i + 1}`}>
                  <div>
                    <CardTitle>{t.title}</CardTitle>
                    <Stars>
                      {Array.from({length: 5}).map((_, idx) => (
                        <span key={idx}>{idx < t.stars ? "★" : "☆"}</span>
                      ))}
                    </Stars>
                    <CardText>{t.text}</CardText>
                  </div>
                  <UserRow>
                    <Avatar src={t.avatar} alt={t.name} width={70} height={70} decoding="async" />
                    <UserInfo>
                      <UserName>{t.name}</UserName>
                      <UserRole>{t.role}</UserRole>
                    </UserInfo>
                  </UserRow>
                </Card>
              );
            })
        )}
      </Grid>
      <BottomBar>
        <BarLeft>
          <BarAvatars>
            {barAvatars.map((img, idx) => (
              <img
                key={idx}
                src={img}
                alt={`avatar-${idx}`}
                width={86}
                height={86}
                loading="lazy"
                decoding="async"
              />
            ))}
          </BarAvatars>
          <BarText>{barText}</BarText>
        </BarLeft>
        <BarButton onClick={() => setShowAll(!showAll)}>
          {showAll ? showLessText : viewAllText}
        </BarButton>
      </BottomBar>
    </GallerySection>
  );
};

const GallerySection = styled.section`
  width: 100%;
  background: ${colors.white};
  margin-top: 10rem;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media(max-width: 768px){
    margin:0;
  }
`;

const TopSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const TopIcon = styled.img`
  width: 2.4rem;
  height: 2.4rem;
`;

const TopText = styled.span`
  font-size: 2rem;
  font-weight: 500;
  color: ${colors.pastelPurple};
  font-family: "Lato";
`;

const GalleryHeading = styled.h2`
  font-family: 'DM Serif Display';
  font-size: 5rem;
  font-weight: 400;
  text-align: center;
  margin-bottom: 3.6rem;
  color: ${colors.eerieBlack};

  @media (max-width: 768px) {
    font-size: 2.7rem;
    max-width: 65%;
    line-height: 1.2;
  }
`;

const Grid = styled.div`
  width: 90%;
  max-width: 125rem;
  column-count: 3;
  column-gap: 2rem;
  margin-bottom: 4rem;

  @media (max-width: 992px) {
    column-count: 2;
  }

  @media (max-width: 768px) {
    column-count: 1;
    width: 85%;
    gap: 1.6rem;
    margin-bottom: 3.55rem;
  }
`;

const Card = styled.div`
  background: ${colors.white};
  border-radius: 2rem;
  box-shadow: 0 2px 16px rgba(0,0,0,0.06);
  padding: 2.4rem 2rem;
  display: flex;
  font-size: 1.6rem; 
  font-weight: 600; 
  font-family: 'Lato';
  flex-direction: column;
  justify-content: space-between;
  box-sizing: border-box;
  break-inside: avoid; 
  page-break-inside: avoid; 
  margin-bottom: 4rem;
  
  &:nth-child(3n+1) {
    transform: scale(1.02);
  }
  
  &:nth-child(5n+1) {
    transform: scale(0.98);
  }

  @media (max-width: 768px) {
    padding: 2rem;
    font-size: 1.8rem;
    width: 100%;
    margin: 0 0 2rem 0;
    transform: none !important;
  }
`;

const CardTitle = styled.div`
  font-size: 1.8rem;
  font-weight: 400;
  line-height: 1.8rem;
  font-family: 'Lato';
  margin-bottom: .8rem;
  color: ${colors.eerieBlack};
`;

const Stars = styled.div`
  color: #FFC500;
  font-size: 1.8rem;
  margin-bottom: 3rem;
  display: flex;
  align-items: center;
  
  span {
    margin-right: 0.2rem;
  }
`;

const CardText = styled.div`
  color: ${colors.textGray};
  font-size: 1.6rem;
  font-family: 'Lato';
  font-weight: 400;
  line-height: 1.5;
  margin-bottom: 3rem;
  overflow: hidden;
  
  @media (max-width: 768px) {
    font-size: 1.4rem;
  }
`;

const UserRow = styled.div`
  display: flex;
  align-items: center;
  margin-top: auto;
  padding-top: 1rem;
`;

const Avatar = styled.img`
  width: 4.4rem;
  height: 4.4rem;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 1.2rem;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const UserName = styled.div`
  font-size: 1.6rem;
  font-weight: 500;
  color: ${colors.black};
  font-family: 'Lato';
`;

const UserRole = styled.div`
  font-size: 1.4rem;
  font-weight: 400;
  color: ${colors.textGray};
  font-family: 'Lato';
`;

const BottomBar = styled.div`
  background: ${colors.textBlack};
  border-radius: 10rem;
  padding: 1.8rem 3.2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 2px 16px rgba(0,0,0,0.08);

  @media (max-width: 768px) {
    display: none;
  }
`;

const BarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1.6rem;
`;

const BarAvatars = styled.div`
  display: flex;
  align-items: center;

  img {
    width: 5.4rem;
    height: 5.4rem;
    border-radius: 50%;
    margin-left: -10px;
    &:first-child { margin-left: 0; }
  }
`;

const BarText = styled.div`
  color: ${colors.white};
  font-size: 2rem;
  font-family: 'Lato';
  font-weight: 500;
  margin-left: 1.2rem;
`;

const BarButton = styled.button`
  background: transparent;
  font-family: 'Lato';
  color: ${colors.white};
  border: solid;
  border-radius: 100rem;
  padding: 1rem 2.8rem;
  font-size: 1.8rem;
  font-weight: 700;
  cursor: pointer;
  margin-left: 2.4rem;
  
  @media (max-width: 768px) {
    margin-left: 0;
    width: 100%;
  }
`;
export default TestimonialBento;
